import { AbsenceFilesRequest } from './../contracts/requests/create-or-update-absence-request';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CreateOrUpdateAbsenceRequest } from '../contracts/requests/create-or-update-absence-request';
import { AbsenceDto } from '../models/dtos/absence-dto';
import { Filter } from '../models/common/filter';
import { AbsenceLimitsDto } from '../models/dtos/absence-limits-dto';
import { IPagedResult } from '../shared/models/PagedResult';
import { APP_CONFIG } from '../app-config.token';
import { AppConfig } from '../app-config';
import { SubordinatesNode } from '../models/dtos/subordinates-node-dto';
import { AbsencesPerYearDto } from '../models/dtos/absences/absences-per-year-dto';
import { SubstituteWorekerDto } from '../models/dtos/substitute-worker-dto';
import { GetSubstituteWorkersRequest } from '../contracts/requests/get-substitute-workers-request';
import { GetAbsenceApprovalProgressRequest } from '../contracts/requests/get-absence-approval-progress-request';
import { AbsenceApprovalProgressStepDto } from '../models/dtos/absence-approval-progress-step-dto';

@Injectable({
  providedIn: 'root',
})
export class AbsenceService {
  private http = inject(HttpClient);
  private config = inject<AppConfig>(APP_CONFIG);

  private readonly API_URL = this.config.resourceApiURI + '/absences';

  constructor() { }

  getAbsence(id: number) {
    const url = `${this.API_URL}/${id}`;
    return this.http.get<AbsenceDto>(url).pipe(
      map((result) => ({
        ...result,
        StartDate: result.StartDate && new Date(result.StartDate + 'Z'),
        EndDate: result.EndDate && new Date(result.EndDate + 'Z'),
      })),
    );
  }

  acceptAbsence(id: number) {
    const url = `${this.API_URL}/${id}/accept`;
    return this.http.post<number>(url, null);
  }

  backToDraftAbsence(id: number) {
    const url = `${this.API_URL}/${id}/backToDraft`;
    return this.http.post<number>(url, null);
  }

  confirmAbsence(id: number) {
    const url = `${this.API_URL}/${id}/confirm`;
    return this.http.post<number>(url, null);
  }

  createDraftAbsence(request: CreateOrUpdateAbsenceRequest) {
    const url = this.API_URL;
    return this.http.post<number>(url, request);
  }

  updateAbsence(absenceId: number, request: CreateOrUpdateAbsenceRequest) {
    const url = `${this.API_URL}/${absenceId}`;
    return this.http.put<number>(url, request);
  }

  updateAbsenceSubstituteWorker(absenceId: number, substituteWorkerId: number) {
    const url = `${this.API_URL}/${absenceId}/substituteWorker`;
    return this.http.put<number>(url, { SubstituteWorkerId: substituteWorkerId });
  }

  sendToApproval(id: number) {
    const url = `${this.API_URL}/${id}/sendToApproval`;
    return this.http.post<number>(url, {});
  }

  deleteAbsence(id: number) {
    const url = `${this.API_URL}/${id}`;
    return this.http.delete(url);
  }

  getAbsences(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters?: Filter[],
    month?: number,
    year?: number,
  ): Observable<IPagedResult<AbsenceDto>> {
    const body = {
      Page: page || 1,
      Count: count || 10,
      SortingDirection: sortingDirection,
      SortingField: sortingField,
      FilterString: filters && filters.length ? JSON.stringify(filters) : '[]',
    };
    const url = this.API_URL + '/getAbsences';
    return this.http.post<IPagedResult<AbsenceDto>>(url, body).pipe(
      map((pagedResult) => ({
        ...pagedResult,
        Results: pagedResult.Results.map((a) => ({
          ...a,
          StartDate: a.StartDate && new Date(a.StartDate + 'Z'),
          EndDate: a.EndDate && new Date(a.EndDate + 'Z'),
          SentToApprovalOn: a.SentToApprovalOn && new Date(a.SentToApprovalOn + 'Z'),
          AcceptedOn: a.AcceptedOn && new Date(a.AcceptedOn + 'Z'),
          RejectedOn: a.RejectedOn && new Date(a.RejectedOn + 'Z'),
        })),
      })),
    );
  }

  getAbsenceByWorkerId(
    id: number,
    workerAgreementId: number | null,
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
  ): Observable<IPagedResult<AbsenceDto>> {
    let params = new HttpParams()
      .set('workerAgreementId', `${workerAgreementId || ''}`)
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection);

    const url = `${this.API_URL}/workers/${id}`;
    return this.http.get<IPagedResult<AbsenceDto>>(url, { params: params }).pipe(
      map((pagedResult) => ({
        ...pagedResult,
        Results: pagedResult.Results.map((a) => ({
          ...a,
          StartDate: a.StartDate && new Date(a.StartDate + 'Z'),
          EndDate: a.EndDate && new Date(a.EndDate + 'Z'),
          SentToApprovalOn: a.SentToApprovalOn && new Date(a.SentToApprovalOn + 'Z'),
          AcceptedOn: a.AcceptedOn && new Date(a.AcceptedOn + 'Z'),
          RejectedOn: a.RejectedOn && new Date(a.RejectedOn + 'Z'),
        })),
      })),
    );
  }

  rejectAbsence(id: number) {
    const url = `${this.API_URL}/${id}/reject`;
    return this.http.post<number>(url, null);
  }

  getAbsenceLimitsByAgreementId(workerAgreementId: number | null): Observable<AbsenceLimitsDto[]> {
    const url = `${this.API_URL}/agreement/${workerAgreementId}/absenceLimits`;
    return this.http.get<AbsenceLimitsDto[]>(url);
  }

  getWorkerAbsences(
    workerId: number,
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
  ): Observable<IPagedResult<AbsenceDto>> {
    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection);

    const url = `${this.API_URL}/${workerId}/absences`;

    return this.http.get<IPagedResult<AbsenceDto>>(url, { params: params }).pipe(
      map(
        (res) =>
          <IPagedResult<AbsenceDto>>{
            ...res,
            Results: res.Results.map((r) => ({ ...r, StartDate: new Date(r.StartDate + 'Z'), EndDate: new Date(r.EndDate + 'Z') })),
          },
      ),
    );
  }

  saveAbsenceFiles(absenceId: number, request: AbsenceFilesRequest) {
    const url = `${this.API_URL}/${absenceId}/saveAbsenceFiles`;
    return this.http.post<number>(url, request);
  }

  getSupervaiorsFilterOptions() {
    const url = `${this.API_URL}/filter/subordinates`;
    return this.http.get<SubordinatesNode[]>(url);
  }

  cancelAbsence(id: number) {
    const url = `${this.API_URL}/${id}/cancel`;
    return this.http.post<number>(url, null);
  }

  getAbsencesPerYear(
    year: number,
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters?: Filter[],
  ): Observable<IPagedResult<AbsencesPerYearDto>> {
    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection)
      .set('filterString', JSON.stringify(filters));

    const url = `${this.API_URL}/breakdown/${year}`;

    return this.http.get<IPagedResult<AbsencesPerYearDto>>(url, { params }).pipe(
      map((pagedResult) => ({
        ...pagedResult,
        Results: pagedResult.Results.map((a) => ({
          ...a,
          EmploymentDateFrom: a.EmploymentDateFrom && new Date(a.EmploymentDateFrom + 'Z'),
          EmploymentDateTo: a.EmploymentDateTo && new Date(a.EmploymentDateTo + 'Z'),
        })),
      })),
    );
  }

  getAbsenceSettingsByWorkerAgreementId(workerAgreementId: number): Observable<any> {
    const url = `${this.API_URL}/agreement/${workerAgreementId}/settings`;
    return this.http.get<any>(url);
  }

  getSubstituteWorkers(workerAgreementId: number, request: GetSubstituteWorkersRequest): Observable<SubstituteWorekerDto[]> {
    const url = `${this.API_URL}/agreement/${workerAgreementId}/substituteWorkers`;
    return this.http.post<SubstituteWorekerDto[]>(url, request);
  }

  getAbsenceApprovalProgress(request: GetAbsenceApprovalProgressRequest): Observable<AbsenceApprovalProgressStepDto[]> {
    const url = `${this.API_URL}/approvalProgress`;
    return this.http.post<AbsenceApprovalProgressStepDto[]>(url, request);
  }
}
